import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Enhanced Tooltip Component
 * Provides accessible tooltips with multiple positioning options
 */
const EnhancedTooltip = ({
  children,
  content,
  position = 'top', // 'top', 'bottom', 'left', 'right', 'auto'
  trigger = 'hover', // 'hover', 'click', 'focus', 'manual'
  delay = 200,
  hideDelay = 100,
  disabled = false,
  className = '',
  tooltipClassName = '',
  maxWidth = '200px',
  showArrow = true,
  interactive = false,
  offset = 8,
  zIndex = 50
}) => {
  const { theme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState(position);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  /**
   * Calculate optimal tooltip position
   */
  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current || position !== 'auto') {
      return position;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Check available space in each direction
    const spaceTop = triggerRect.top;
    const spaceBottom = viewport.height - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewport.width - triggerRect.right;

    // Determine best position based on available space
    if (spaceTop >= tooltipRect.height + offset) return 'top';
    if (spaceBottom >= tooltipRect.height + offset) return 'bottom';
    if (spaceRight >= tooltipRect.width + offset) return 'right';
    if (spaceLeft >= tooltipRect.width + offset) return 'left';

    // Fallback to position with most space
    const maxSpace = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight);
    if (maxSpace === spaceTop) return 'top';
    if (maxSpace === spaceBottom) return 'bottom';
    if (maxSpace === spaceRight) return 'right';
    return 'left';
  };

  /**
   * Show tooltip
   */
  const showTooltip = () => {
    if (disabled || !content) return;

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    showTimeoutRef.current = setTimeout(() => {
      setActualPosition(calculatePosition());
      setIsVisible(true);
    }, delay);
  };

  /**
   * Hide tooltip
   */
  const hideTooltip = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, hideDelay);
  };

  /**
   * Handle mouse enter
   */
  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      showTooltip();
    }
  };

  /**
   * Handle mouse leave
   */
  const handleMouseLeave = () => {
    if (trigger === 'hover' && !interactive) {
      hideTooltip();
    }
  };

  /**
   * Handle click
   */
  const handleClick = () => {
    if (trigger === 'click') {
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip();
      }
    }
  };

  /**
   * Handle focus
   */
  const handleFocus = () => {
    if (trigger === 'focus') {
      showTooltip();
    }
  };

  /**
   * Handle blur
   */
  const handleBlur = () => {
    if (trigger === 'focus') {
      hideTooltip();
    }
  };

  /**
   * Handle tooltip mouse enter (for interactive tooltips)
   */
  const handleTooltipMouseEnter = () => {
    if (interactive && trigger === 'hover') {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    }
  };

  /**
   * Handle tooltip mouse leave (for interactive tooltips)
   */
  const handleTooltipMouseLeave = () => {
    if (interactive && trigger === 'hover') {
      hideTooltip();
    }
  };

  /**
   * Get tooltip position classes
   */
  const getPositionClasses = () => {
    const baseClasses = 'absolute z-50';
    
    switch (actualPosition) {
      case 'top':
        return `${baseClasses} bottom-full left-1/2 transform -translate-x-1/2 mb-${offset/4}`;
      case 'bottom':
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 mt-${offset/4}`;
      case 'left':
        return `${baseClasses} right-full top-1/2 transform -translate-y-1/2 mr-${offset/4}`;
      case 'right':
        return `${baseClasses} left-full top-1/2 transform -translate-y-1/2 ml-${offset/4}`;
      default:
        return `${baseClasses} bottom-full left-1/2 transform -translate-x-1/2 mb-${offset/4}`;
    }
  };

  /**
   * Get arrow classes
   */
  const getArrowClasses = () => {
    if (!showArrow) return '';

    const arrowColor = theme === 'dark' ? 'border-gray-800' : 'border-gray-900';
    const baseArrow = 'absolute w-0 h-0';

    switch (actualPosition) {
      case 'top':
        return `${baseArrow} top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent ${arrowColor}`;
      case 'bottom':
        return `${baseArrow} bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent ${arrowColor}`;
      case 'left':
        return `${baseArrow} left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent ${arrowColor}`;
      case 'right':
        return `${baseArrow} right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent ${arrowColor}`;
      default:
        return '';
    }
  };

  /**
   * Cleanup timeouts on unmount
   */
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Handle escape key to close tooltip
   */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isVisible) {
        hideTooltip();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisible]);

  /**
   * Handle click outside to close tooltip
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        trigger === 'click' &&
        isVisible &&
        triggerRef.current &&
        tooltipRef.current &&
        !triggerRef.current.contains(event.target) &&
        !tooltipRef.current.contains(event.target)
      ) {
        hideTooltip();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, trigger]);

  if (!content || disabled) {
    return children;
  }

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="cursor-help"
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`${getPositionClasses()} z-${zIndex}`}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          role="tooltip"
          aria-hidden={!isVisible}
        >
          <div
            className={`px-3 py-2 text-sm rounded-lg shadow-lg border ${
              theme === 'dark'
                ? 'bg-gray-800 text-gray-100 border-gray-700'
                : 'bg-gray-900 text-white border-gray-800'
            } ${tooltipClassName}`}
            style={{ maxWidth }}
          >
            {typeof content === 'string' ? (
              <span>{content}</span>
            ) : (
              content
            )}
          </div>
          
          {showArrow && (
            <div className={getArrowClasses()} />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Help Icon with Tooltip
 */
export const HelpTooltip = ({ 
  content, 
  position = 'top',
  className = '',
  iconClassName = ''
}) => {
  const { theme } = useTheme();
  
  return (
    <EnhancedTooltip
      content={content}
      position={position}
      trigger="hover"
      className={className}
    >
      <svg 
        className={`w-4 h-4 cursor-help ${
          theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'
        } transition-colors ${iconClassName}`}
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
        aria-label="Help"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
      </svg>
    </EnhancedTooltip>
  );
};

/**
 * Info Tooltip
 */
export const InfoTooltip = ({ 
  content, 
  position = 'top',
  className = '',
  iconClassName = ''
}) => {
  const { theme } = useTheme();
  
  return (
    <EnhancedTooltip
      content={content}
      position={position}
      trigger="hover"
      className={className}
    >
      <svg 
        className={`w-4 h-4 cursor-help ${
          theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'
        } transition-colors ${iconClassName}`}
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
        aria-label="Information"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
        />
      </svg>
    </EnhancedTooltip>
  );
};

export default EnhancedTooltip;
